<template>
  <!-- Hidden when GOOGLE_CLIENT_ID is not set (e.g. on-prem deploys) -->
  <div v-if="clientId" class="google-btn-wrapper">
    <v-btn
      class="google-btn"
      type="button"
      :disabled="!ready"
      @click="triggerPrompt"
    >
      <v-icon left>mdi-google</v-icon>
      {{ label }}
    </v-btn>
  </div>
</template>

<script>
/**
 * Renders a custom Google login button that triggers OAuth code redirect.
 *
 * One Tap is shown automatically by GoogleOneTap when available.
 *
 * The button is automatically hidden when GOOGLE_CLIENT_ID is not configured
 * (e.g. on-prem deployments that don't need Google auth).
 *
 * Props:
 *   - text: "signin_with" | "signup_with" | "continue_with" (default)
 *
 * Events:
 *   - error(msg): emitted on failure
 */

import {
  GOOGLE_CLIENT_ID,
  initCodeClient,
  initializeGoogle,
  loadScript
} from "../index";

export default {
  name: "GoogleLoginButton",
  props: {
    text: {
      type: String,
      default: "continue_with",
      validator: v => ["signin_with", "signup_with", "continue_with"].includes(v)
    }
  },
  data: () => ({
    clientId: GOOGLE_CLIENT_ID,
    initialized: false,
    ready: false
  }),
  computed: {
    label() {
      if (this.text === "signin_with") return "Sign in with Google";
      if (this.text === "signup_with") return "Sign up with Google";
      return "Continue with Google";
    }
  },
  mounted() {
    if (this.clientId) {
      loadScript()
        .then(() => {
          this.initialize();
          this.ready = true;
        })
        .catch(() => {
          this.$emit("error", "Google authentication unavailable");
        });
    }
  },
  methods: {
    initialize() {
      if (this.initialized || !window.google || !window.google.accounts) return;
      this.initialized = true;
      initializeGoogle(this.clientId);
    },
    buildLoginRedirectUri() {
      return `${window.location.origin}/auth/google-login/`;
    },
    buildState() {
      const next = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      return next || "/";
    },
    requestRedirectCode() {
      const codeClient = initCodeClient({
        clientId: this.clientId,
        redirectUri: this.buildLoginRedirectUri(),
        scope: "openid email profile",
        state: this.buildState()
      });
      if (!codeClient || !codeClient.requestCode) {
        this.$emit("error", "Google authentication unavailable");
        return;
      }
      codeClient.requestCode();
    },
    triggerPrompt() {
      loadScript()
        .then(() => {
          this.initialize();
          this.requestRedirectCode();
        })
        .catch(() => {
          this.$emit("error", "Google authentication unavailable");
        });
    }
  }
};
</script>

<style scoped>
.google-btn-wrapper {
  display: flex;
  justify-content: center;
}

.google-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  background-color: #1f1f1f !important;
  color: #fff !important;
  width: 100%;
  max-width: 360px;
}
</style>
